import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { matchPath } from 'react-router-dom'
import { withRouterProps } from './utils/with-router-props'

import { Helmet } from 'react-helmet'

import '../assets/scss/app.scss'

import { routes } from '../constants/routes'
import { saveNavClick } from '../reducers/nav'
import { saveWindowSize } from '../reducers/ui'

import MusicPlayerWrapper from './audio-player/music-player-wrapper'
import AppNav from './layout/app-nav'
import Footer from './layout/footer'
import IndexWrapper from './wrappers/index-wrapper'

// Home Component
import Home from './pages/home'
import Preloader from './pages/preloader'

// Index Components
import ClayvilleIndex from './clayville/index'
import ClayvilleView from './clayville/story'
import ContentPage from './content/page'
import KeyIssuesIndex from './key-issues/index'
import KeyIssueOveriew from './key-issues/overview'
import KeyIssueView from './key-issues/view'
import MurphysIndex from './murphys/index'
import MurphyStory from './murphys/story'
import ResourcesIndex from './resources/index'
import SiteGuidesIndex from './site-guides/index'
import StyleGuideIndex from './style-guide/index'

import DevTools from './utils/dev-tools'

import { library } from '@fortawesome/fontawesome-svg-core'
import {
    faTimes, faCheck, faCaretDown, faEdit, faArrowRight, faArrowLeft, faArrowUp, faArrowDown, faSearch, faInfoCircle, faCheckCircle, faSpinner, faPlus, faMinus, faCaretUp,
    faToggleOn, faToggleOff, faSync, faSort, faBars, faVolumeLow, faVolumeHigh, faVolumeMute, faMap, faChevronDown, faChevronUp, faChevronRight, faChevronLeft, faPlay, faShareNodes
} from '@fortawesome/free-solid-svg-icons'
import { saveVisitedRoute } from '../reducers/visited-routes'
import CookieConsent from './common/cookie-consent'
import { setHasInteracted } from '../reducers/sound'


library.add(
    faTimes, faCheck, faCaretDown, faEdit, faArrowRight, faArrowLeft, faArrowUp, faArrowDown, faSearch, faInfoCircle, faCheckCircle, faSpinner, faPlus, faMinus, faCaretUp,
    faToggleOn, faToggleOff, faSync, faSort, faBars, faVolumeLow, faVolumeHigh, faVolumeMute, faMap, faChevronDown, faChevronUp, faChevronRight, faChevronLeft, faPlay, faShareNodes
)

const mapStateToProps = (state) => {
    const { nav, ui, metaData } = state
    return {
        navState: nav,
        uiState: ui,
        metaData: metaData
    }
}

const App = ({ dispatch, location, navState, uiState, metaData }) => {
    const activePath = location.pathname
    const searchParams = window.location.search

    function handleWindowSizeChange() {
        const newWidth = window.innerWidth
        const newHeight = window.innerHeight
        dispatch(saveWindowSize({ width: newWidth, height: newHeight }))
    }

    function handleGenericClickEvent() {
        dispatch(setHasInteracted())
    }

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange)
        window.addEventListener('click', handleGenericClickEvent)
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange)
            window.removeEventListener('click', handleGenericClickEvent)
        }
    }, [])

    useEffect(() => {
        // given a routes obj that has params of path, label and component, find the route that matches the activePath
        // const activeRoute = Object.values(routes).find(route => route.path === activePath)
        const activeRoute = Object.values(routes).find(route => matchPath({ path: route.path }, activePath))
        const activeParams = activeRoute && activePath ? matchPath({ path: activeRoute?.path }, activePath)?.params || {} : {}
        if (navState.activeComponent === 'Preloader') {
            // let the preloader set the initial state
        } else if (activeRoute &&
            (activeRoute.component === 'Content Page' ||
                (activeRoute.component === 'Character Story' && activeRoute.component !== navState.activeComponent) ||
                // DREG: Do we need the trailing OR statement here as it seems to be caught in the line above?
                (activeRoute.component === 'Character Story' && navState.activeUrl.indexOf(activeParams.character) === -1) ||
                activeRoute.component !== navState.activeComponent)) {
            setTimeout(() => {
                // make sure the scroll to top on window and containers
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
                let app = document.getElementById('app')
                if (app) {
                    app.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    })
                }
            }, 100)
            dispatch(saveNavClick(
                {
                    url: activePath,
                    preLoadingComponent: activeRoute.component,
                    animatingInComponent: null,
                    animatingOutComponent: navState.activeComponent
                }
            ))

            const routeData = {
                route: { toLink: activePath.replace(/\/$/, ''), date_visited: new Date().getTime() }
            }
            dispatch(saveVisitedRoute(routeData))
        }
    }, [dispatch, activePath])

    const renderSEOData = () => {
        let pageTitle = 'Way Outta No Way'
        let pageDescription = 'Racism devastates Black lives. It blocks all of us from living in a just, peaceful society. Seeing racism as a system empowers us to change it.'
        let pageImage = `${window.location.origin}/wonw-default-share.jpg`

        // Create canonical URL: strip query parameters and trailing slash
        let canonicalPath = window.location.origin + window.location.pathname.replace(/\/$/, '')
        // if window.location.search starts with '?category=' add it to canonicalPath
        if (window.location.search.startsWith('?category=')) {
            canonicalPath = canonicalPath + window.location.search
        }

        let searchPath = activePath.replace(/\/$/, '')
        if (searchPath === '') {
            searchPath = '/homepage'
        }
        const metaDataPage = metaData.find(page => page.alias === searchPath)
        if (metaDataPage) {
            pageTitle = metaDataPage.pageTitle
            pageDescription = metaDataPage.metaDescription
            pageImage = metaDataPage?.metaImage || pageImage
        }
        return (
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <link rel="canonical" href={canonicalPath} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:image" content={pageImage?.url} />
            </Helmet>
        )
    }

    return (
        <>
            {renderSEOData()}
            <MusicPlayerWrapper />
            <div id="app" className={`${uiState.deviceType} ${uiState.orientation} ${uiState.deviceHeight}`}>
                <AppNav />
                <MurphysIndex />
                <MurphyStory />
                <ClayvilleIndex />
                <ClayvilleView />
                <KeyIssuesIndex searchParams={searchParams} />
                <KeyIssueOveriew />
                <KeyIssueView />
                <ResourcesIndex />
                <SiteGuidesIndex />
                <ContentPage />
                <IndexWrapper navState={navState} componentName="Style Guide"><StyleGuideIndex /></IndexWrapper>
                <Home />
                <Preloader />
                <DevTools />
                <CookieConsent />
                <Footer />
            </div>
        </>
    )
}

export default withRouterProps(connect(mapStateToProps)(App))
